import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import {
  addEventListeners,
  type EventElement,
  type Events,
  isDefined,
  isNotDefined,
  removeEventListeners,
} from '@enterprise/core';

import type { ListenerOptions } from './types/listener.options';

/**
 * React hook for adding and removing event listeners to a specified DOM element or the window object.
 *
 * This hook manages the lifecycle of event listeners, ensuring they are properly added and removed
 * based on the provided options and dependencies. It supports listening to one or multiple event names,
 * and allows for disabling the listener dynamically.
 *
 * @template TEvent - The type of the event(s) to listen for.
 * @template TElement - The type of the element to attach the event listener to.
 *
 * @param {ListenerOptions<TEvent, TElement>} options - The configuration options for the event listener.
 * @param {boolean} [options.disabled=false] - If true, the event listener will be removed or not added.
 * @param {React.RefObject<TElement>} [options.element] - The ref of the element to attach the event listener to.
 * Defaults to `window` if not provided.
 * @param {string | string[]} options.eventName - The event name(s) to listen for.
 * @param {(event: TEvent) => void} options.handler - The event handler function.
 * @param {AddEventListenerOptions | boolean} [options.listenerOptions] - Options to pass to `addEventListener`.
 *
 * @throws {Error} If no event name is provided.
 *
 * @returns {void}
 *
 * @example
 * useEventListener({
 *   element: buttonRef,
 *   eventName: 'click',
 *   handler: handleClick,
 * });
 */
export function useEventListener<TEvent extends Events[keyof Events], TElement extends EventElement>({
  disabled = false,
  element,
  eventName,
  handler,
  listenerOptions: options,
}: ListenerOptions<TEvent, TElement>) {
  const savedHandler = useRef(handler);
  const eventNames = useMemo(() => {
    return Array.isArray(eventName) ? eventName : [eventName];
  }, [eventName]);

  if (eventNames.length <= 0) {
    throw new Error('At least 1 event name is required.');
  }

  useLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    let targetElement: TElement | Window;

    if (isDefined(element) && isDefined(element.current)) {
      targetElement = element.current;
    } else {
      targetElement = window;
    }

    if (isNotDefined(targetElement.addEventListener)) {
      return;
    }

    if (disabled) {
      removeEventListeners(targetElement, handler, options, ...eventNames);
    } else {
      addEventListeners(targetElement, handler, options, ...eventNames);
    }

    return () => {
      removeEventListeners(targetElement, handler, options, ...eventNames);
    };
  }, [disabled, element, eventNames, handler, options]);
}
